import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Select,
  Table,
  Card,
  Space,
  Alert,
  Avatar,
  message,
  Row,
  Col,
  Typography,
} from "antd";
import {
  FilterOutlined,
  FileExcelOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { fetchStreamers, fetchFilterOptions } from "./utils/api";
import * as XLSX from "xlsx";
import  { getCountryName } from "./utils/countryNames";
const { Text,Title } = Typography;
const { Option } = Select;


const HomePage = (props) => {
  const [streamers, setStreamers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    region: "all",
    platform: "all",
    game: "0",
    category: "game",
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [resultsTitle, setResultsTitle] = useState(
    "全球 全平台 全部游戏 TOP100主播"
  );

  // 初始化筛选数据
  const initializeFilterData = useCallback(async () => {
    try {
      const filterRes = await fetchFilterOptions();

      // 设置地区和游戏数据
      if (filterRes?.regions) {
        setRegions(filterRes.regions);
        // 根据当前选择的地区，填充平台列表
        let availablePlatforms = [];
        if (filters.region === "all") {
          const seen = new Set();
          filterRes.regions.forEach((r) => {
            (r.platforms || []).forEach((p) => {
              const pid = String(p.id);
              if (!seen.has(pid)) {
                seen.add(pid);
                availablePlatforms.push(p);
              }
            });
          });
        } else {
          const currentRegion = filterRes.regions.find(
            (r) => String(r.id) === String(filters.region)
          );
          availablePlatforms = currentRegion?.platforms || [];
        }
        setPlatforms(availablePlatforms);
      }
      if (filterRes?.games) {
        setGames(filterRes.games);
      }
    } catch (error) {
      console.error("Error loading filter data:", error);
      setError(error?.message || "加载筛选数据失败");
    }
  }, [filters.region]);

  // 计算国家码
  const getCountryCodes = useCallback(() => {
    if (filters.region === "all") return undefined;
    const r = regions.find((rr) => String(rr.id) === String(filters.region));
    return r?.countryCodes?.length ? r.countryCodes : undefined;
  }, [filters.region, regions]);

  const getPlatformCodes = useCallback(() => {
    if (filters.platform === "all") return undefined;
    const p = platforms.find(
      (pp) => String(pp.id) === String(filters.platform)
    );
    return p?.code ? [p.code] : undefined;
  }, [filters.platform, platforms]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const countryCodes = getCountryCodes();
      const platformCodes = getPlatformCodes();
      const gameId = filters.game === "0" ? undefined : filters.game;

      const data = await fetchStreamers({
        countryCodes,
        platformCodes,
        game: gameId,
        category: filters.category,
        limit: 100,
      });

      if (data?.streamers) {
        setStreamers(data.streamers);
      }

      // 更新结果标题
      const regionName =
        filters.region === "all"
          ? "全球"
          : regions.find((r) => String(r.id) === String(filters.region))
              ?.name || "全球";
      const platformName =
        filters.platform === "all"
          ? "全平台"
          : platforms.find((p) => String(p.id) === String(filters.platform))
              ?.name || "全平台";
      const gameName =
        filters.game === "0"
          ? "全部游戏"
          : games.find((g) => String(g.id) === String(filters.game))?.cName ||
            "全部游戏";

      setResultsTitle(`${regionName} ${platformName} ${gameName} TOP100主播`);
    } catch (error) {
      console.error("Error loading data:", error);
      setError(error?.message || "加载数据失败");
    } finally {
      setLoading(false);
    }
  }, [filters, getCountryCodes, getPlatformCodes, regions, platforms, games, filters.category]);

  // 应用筛选
  const applyFilters = () => {
    loadData();
  };

  // 导出Excel
  const handleExport = async () => {
    try {
      if (!streamers.length) {
        message.warning("暂无数据可导出");
        return;
      }

      // 构建表格数据
      const rows = streamers.map((streamer, index) => [
        index + 1, // 排名
        streamer.name || "", // 名称
        streamer.email || "", // 自定义URL
        getCountryName(streamer.country) || "", // 国家
        streamer.platform || "", // 平台
        streamer.home_page ||
          streamer.homepage ||
          `https://www.youtube.com/channel/${streamer.id}`, // 主页链接
        toW(streamer.follower_count), // 粉丝量(w)
        toM(streamer.total_play_count), // 总播放量(M)
        toW(streamer.avg_play_count), // 平均播放(w)
        toW(streamer.recent_play_count), // 最近投稿(w)
        toW(streamer.highest_play_count), // 最高播放(w)
      ]);

      // 表头
      const header = [
        "排名",
        "名称",
        "邮箱",
        "国家",
        "平台",
        "主页链接",
        "粉丝量(w)",
        "总播放量(M)",
        "平均播放(w)",
        "最近投稿(w)",
        "最高播放(w)",
      ];

      // 创建工作表
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);

      // 设置列宽
      ws["!cols"] = [
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 65 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];

      // 生成文件名
      const regionName =
        filters.region === "all"
          ? "全球"
          : regions.find((r) => String(r.id) === String(filters.region))
              ?.name || "全球";
      const platformName =
        filters.platform === "all"
          ? "全平台"
          : platforms.find((p) => String(p.id) === String(filters.platform))
              ?.name || "全平台";
      const gameName =
        filters.game === "0"
          ? "全部游戏"
          : games.find((g) => String(g.id) === String(filters.game))?.cName ||
            "全部游戏";

      const filename =
        `Top100_${regionName}_${platformName}_${gameName}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`.replace(/[\\/:*?"<>|]/g, "_");

      // 创建工作簿并下载
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Top100");
      XLSX.writeFile(wb, filename);

      message.success("导出成功！");
    } catch (error) {
      console.error("Export error:", error);
      message.error("导出失败：" + (error?.message || "未知错误"));
    }
  };

  // 格式化数字
  const toW = (num) => {
    if (!num) return "0";
    return (num / 10000).toFixed(1);
  };

  const toM = (num) => {
    if (!num) return "0";
    return (num / 1000000).toFixed(1);
  };

  // 获取平台图标
  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "YouTube":
        return <i className="fa fa-youtube text-red-500"></i>;
      case "Twitch":
        return <i className="fa fa-twitch text-purple-500"></i>;
      case "TikTok":
        return <i className="fa fa-music text-black"></i>;
      case "抖音":
        return <i className="fa fa-music text-red-500"></i>;
      case "Bilibili":
        return <i className="fa fa-play-circle text-blue-500"></i>;
      case "快手":
        return <i className="fa fa-video-camera text-orange-500"></i>;
      default:
        return <i className="fa fa-globe text-gray-500"></i>;
    }
  };

  useEffect(() => {
    initializeFilterData();
  }, [initializeFilterData]);

  useEffect(() => {
    loadData();
  }, []);

  const columns = [
    {
      title: "排名",
      key: "rank",
      width: 80,
      align: "center",
      render: (_, __, index) => <Text strong>{index + 1}</Text>,
    },
    {
      title: "主播信息",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (name, record) => (
        <Space>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Avatar src={record.avatar} size={40} />
            {record.email && (
              <MailOutlined
                style={{
                  color: "#1890ff",
                  marginRight: 0,
                }}
                onClick={() => window.open(`mailto:${record.email}`)}
              />
            )}
          </div>

          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: "国家",
      dataIndex: "country",
      key: "country",
      width: 100,
      align: "center",
      render: (country) => <Text>{getCountryName(country)}</Text>,
    },
    {
      title: "平台",
      dataIndex: "platform",
      key: "platform",
      width: 100,
      align: "center",
      render: (platform) => (
        <Space>
          {getPlatformIcon(platform)}
          <Text>{platform}</Text>
        </Space>
      ),
    },
    {
      title: "粉丝量(w)",
      dataIndex: "follower_count",
      key: "follower_count",
      width: 120,
      align: "center",
      render: (count) => <Text strong>{toW(count)}</Text>,
    },
    {
      title: "主页链接",
      dataIndex: "id",
      key: "homepage",
      width: 100,
      align: "center",
      render: (id, record) => (
        <Button
          type="link"
          size="small"
          onClick={() =>
            window.open(
              record?.home_page || `https://www.youtube.com/channel/${id}`,
              "_blank"
            )
          }
        >
          访问
        </Button>
      ),
    },
    {
      title: "总播放量(M)",
      dataIndex: "total_play_count",
      key: "total_play_count",
      width: 120,
      align: "center",
      render: (count) => <Text>{toM(count)}</Text>,
    },
    {
      title: "平均播放(w)",
      dataIndex: "avg_play_count",
      key: "avg_play_count",
      width: 120,
      align: "center",
      render: (count) => <Text>{toW(count)}</Text>,
    },
    {
      title: "最近投稿(w)",
      dataIndex: "recent_play_count",
      key: "recent_play_count",
      width: 120,
      align: "center",
      render: (count, record) =>
        record.recent_video_url ? (
          <Button
            type="link"
            size="small"
            onClick={() => window.open(record.recent_video_url, "_blank")}
          >
            {toW(count)}
          </Button>
        ) : (
          <Text>{toW(count)}</Text>
        ),
    },
    {
      title: "最高播放(w)",
      dataIndex: "highest_play_count",
      key: "highest_play_count",
      width: 120,
      align: "center",
      render: (count, record) =>
        record.highest_video_url ? (
          <Button
            type="link"
            size="small"
            onClick={() => window.open(record.highest_video_url, "_blank")}
          >
            {toW(count)}
          </Button>
        ) : (
          <Text>{toW(count)}</Text>
        ),
    },
  ];

  return (
    <div className="top-tank-container">
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                {resultsTitle}
              </Title>
            </Col>
            <Col>
              <Text>
                根据订阅人数排名，展示各地区、平台和游戏的顶级主播，数据每周更新一次
              </Text>
            </Col>
          </Row>
        }
      >
        {error && (
          <Alert
            message="加载失败"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Row justify="space-between" align="bottom">
              <Col flex="auto">
                <Space size="large" wrap={false}>
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      分类
                    </Text>
                    <Select
                      value={filters.category}
                      onChange={(value) =>
                        setFilters((f) => ({ ...f, category: value }))
                      }
                      style={{ width: 200 }}
                      placeholder="选择分类"
                    >
                      <Option value="game">游戏</Option>
                      <Option value="short">短剧</Option>
                    </Select>
                  </div>
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      地区
                    </Text>
                    <Select
                      value={filters.region}
                      onChange={(value) =>
                        setFilters((f) => ({ ...f, region: value }))
                      }
                      style={{ width: 200 }}
                      placeholder="选择地区"
                    >
                      <Option value="all">全部地区</Option>
                      {regions.map((region) => (
                        <Option key={region.id} value={region.id}>
                          {region.name}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      平台
                    </Text>
                    <Select
                      value={filters.platform}
                      onChange={(value) =>
                        setFilters((f) => ({ ...f, platform: value }))
                      }
                      style={{ width: 200 }}
                      placeholder="选择平台"
                    >
                      <Option value="all">全部平台</Option>
                      {platforms.map((platform) => (
                        <Option key={platform.id} value={platform.id}>
                          {platform.name}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      游戏
                    </Text>
                    <Select
                      value={filters.game}
                      onChange={(value) =>
                        setFilters((f) => ({ ...f, game: value }))
                      }
                      style={{ width: 200 }}
                      placeholder="选择游戏"
                    >
                      <Option value="0">全部游戏</Option>
                      {games.map((game) => (
                        <Option key={game.gameId} value={game.gameId}>
                          {game.cName}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    onClick={applyFilters}
                  >
                    应用筛选
                  </Button>
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleExport}
                  >
                    导出 Excel
                  </Button>
                </Space>
              </Col>
            </Row>
          </Space>
        </Card>

        <div className="results-table">
          <Table
            dataSource={streamers}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={(pag) => {
              setPagination((prev) => ({
                ...prev,
                current: pag.current,
                pageSize: pag.pageSize,
              }));
            }}
            scroll={{ x: 1200 }}
            columns={columns}
          />
        </div>
      </Card>
    </div>
  );
};

export default HomePage;
